import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Divider, Heading } from '@chakra-ui/react';

import { Markdown } from '~/components/markdown';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { EnterpriseResourceContainer } from '~/features/enterprise-resource/components/enterprise-resource-container';
import { EnterpriseResourceForm } from '~/features/enterprise-resource/components/enterprise-resource-form';
import { EnterpriseResourceHero } from '~/features/enterprise-resource/components/enterprise-resource-hero';
import { EnterpriseResourceHeroWithLeadForm } from '~/features/enterprise-resource/components/enterprise-resource-hero-with-lead-form';
import { EnterpriseResourcePerson } from '~/features/enterprise-resource/components/enterprise-resource-person';
import { getEnterpriseLiveEventContent } from '~/features/enterprise-resource/controllers/get-enterprise-live-event-content';
import { EnterpriseLiveEventContent } from '~/features/enterprise-resource/models/enterprise-live-event-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

type EnterpriseLiveEventPageProps = {
  page: Page;
  enterpriseLiveEventContent: EnterpriseLiveEventContent;
};

export default function EnterpriseLiveEventPage({ page, enterpriseLiveEventContent }: EnterpriseLiveEventPageProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const liveEventData = enterpriseLiveEventContent.liveEventData;
  const startTime = liveEventData?.startTimes?.map((startTime) => startTime.time).join(' & ');
  const formData = liveEventData.form;

  function handlePrimaryCtaClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Live Event CTA Clicked',
      label: 'shapes_banner_cta_clicked',
      value: enterpriseLiveEventContent.slug,
    });
  }

  const FormSlotContent = liveEventData.isGated && formData && (
    <EnterpriseResourceForm
      ref={formRef}
      additionalEventProperties={{ liveEventName: enterpriseLiveEventContent.slug }}
      enableResponseModal={true}
      leadCaptureType={liveEventData.leadCaptureType}
      resourceType='live-event'
      startDate={liveEventData.startDate}
      startTimes={liveEventData.startTimes ?? undefined}
      templateForm={formData}
      errorModalProps={{
        title: 'Registration Error',
        message: 'There was an issue registering for this event. Please try again later.',
      }}
      successModalProps={{
        title: 'Registration Successful',
        message: `You are now registered for the "${liveEventData.title}" live event.`,
      }}
    />
  );

  const PersonListContent = (
    <Box
      columnGap='24px'
      display='flex'
      flexWrap='wrap'
      marginTop={{ base: '40px', md: '64px', lg: '120px' }}
      paddingRight={{ base: '0px', lg: formData ? '32px' : '0px' }}
      rowGap='40px'
    >
      {liveEventData.personList.map((person) => (
        <Box key={person.name} flexBasis={{ base: '100%', md: 'calc(50% - 12px)' }}>
          <EnterpriseResourcePerson person={person} />
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseLiveEvent} />
      <PageLayout overflowX='hidden' withoutVerticalPadding>
        {liveEventData.isGated && (
          <EnterpriseResourceHeroWithLeadForm
            contentSlot={PersonListContent}
            description={liveEventData.description}
            formSlot={FormSlotContent}
            relatedCompanies={liveEventData.relatedCompanies}
            resourceTypeTitle={liveEventData.resourceTypeTitle}
            startDate={liveEventData.startDate}
            startTime={startTime}
            title={liveEventData.title}
          />
        )}

        {!liveEventData.isGated && liveEventData.heroImage && liveEventData.primaryCta && (
          <>
            <EnterpriseResourceHero
              description={liveEventData.description}
              image={liveEventData.heroImage}
              primaryCta={liveEventData.primaryCta}
              relatedCompanies={liveEventData.relatedCompanies}
              resourceTypeTitle={liveEventData.resourceTypeTitle}
              startDate={liveEventData.startDate}
              startTime={startTime}
              title={liveEventData.title}
            />

            <EnterpriseResourceContainer>{PersonListContent}</EnterpriseResourceContainer>
          </>
        )}

        <EnterpriseResourceContainer marginBottom={{ base: '80px', md: '120px' }}>
          <Divider marginY={{ base: '40px', md: '64px', lg: '80px' }} />

          <Box display='flex' flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
            <Heading
              as='h2'
              marginBottom={{ base: '40px', lg: '0px' }}
              marginRight={{ base: '0px', lg: '120px' }}
              size='marketing-4xl'
            >
              {liveEventData.contentTitle}
            </Heading>

            <Markdown markdownText={liveEventData.contentDescription} />
          </Box>
        </EnterpriseResourceContainer>

        {liveEventData.shapesBanner && (
          <ShapesBanner
            {...liveEventData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={handlePrimaryCtaClick}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseLiveEventPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise resource slug not found', {
      file: 'src/pages/event/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise resource slug not found');
  }

  const [page, enterpriseLiveEventContent] = await Promise.all([
    getPage('/event'),
    getEnterpriseLiveEventContent(slug),
  ]);

  if (!enterpriseLiveEventContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseLiveEventContent has seo, override default page seo
  if (enterpriseLiveEventContent.seo) {
    page.seo = enterpriseLiveEventContent.seo;
  }

  return {
    props: {
      page,
      enterpriseLiveEventContent,
    },
  };
}
